import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { fetchConsentSummaries } from '../../services/consentService';
import {
  OPTIONAL_CONSENT_TYPES,
  REQUIRED_CONSENT_TYPES,
  type ConsentStatus,
  type ConsentType,
} from '../../types/consent';

/**
 * What a member has agreed to, and how they take it back.
 *
 * Consent §3.4: "Consent may be withdrawn at any time, and withdrawal must be as
 * straightforward as giving consent was." Until now `'revoked'` existed in the
 * type system and nothing could produce it — withdrawal was an email address in
 * the Privacy Notice, which is not as straightforward as the checkbox that took
 * it.
 *
 * §11.5 governs the tone as much as the mechanism: a member who withdraws "is not
 * asked to justify it, is not subjected to a retention approach". So there is no
 * "are you sure you want to lose this?", no benefits reminder, and no second
 * screen designed to change their mind — one confirmation, because the action is
 * irreversible, and it says what happens rather than arguing.
 */

const withdrawConsentFn = httpsCallable<
  { consentType: ConsentType },
  { ok: boolean; consentType: string; confirmation: string; deletion: { applied: boolean } }
>(functions, 'withdrawConsent');

const LABELS: Partial<Record<ConsentType, { title: string; body: string }>> = {
  terms: {
    title: 'The membership terms',
    body: 'What Melio provides, and what it does not.',
  },
  privacy: {
    title: 'The privacy notice',
    body: 'How Melio handles your information.',
  },
  health_data_processing: {
    title: 'Using your health information',
    body: 'So Melio can run your assessment, review your results and follow up.',
  },
  genetics_context: {
    title: 'Genetic testing',
    body: 'A defined panel of markers for context, not a diagnostic genetics service.',
  },
  genetic_data_retention: {
    title: 'Keeping your raw genetic data',
    body: 'So Melio can look at your panel again as medical knowledge develops, without '
      + 'asking you for a new sample.',
  },
};

const STATUS_LABEL: Record<ConsentStatus, string> = {
  granted: 'Agreed',
  declined: 'Declined',
  revoked: 'Withdrawn',
};

export default function ConsentSettings() {
  const { currentUser } = useAuth();
  const [summaries, setSummaries] = useState<Record<string, { status: ConsentStatus; version: string }>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<ConsentType | null>(null);
  const [confirming, setConfirming] = useState<ConsentType | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      setSummaries(await fetchConsentSummaries(currentUser.uid));
    } catch {
      setError('Your consent record could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { void load(); }, [load]);

  const withdraw = async (consentType: ConsentType) => {
    setBusy(consentType);
    setError(null);
    setMessage(null);
    try {
      const res = await withdrawConsentFn({ consentType });
      setMessage(res.data.confirmation);
      setConfirming(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be actioned. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (!currentUser) return null;

  // Required consents are shown but not withdrawable here: §11.4 makes withdrawal a
  // conversation about "which parts of the Service can no longer be provided", and a
  // checkbox cannot have it. The route is a person, and it is offered rather than
  // hidden.
  const rows: Array<{ type: ConsentType; withdrawable: boolean }> = [
    ...REQUIRED_CONSENT_TYPES.map((type) => ({ type, withdrawable: false })),
    ...OPTIONAL_CONSENT_TYPES.map((type) => ({ type, withdrawable: true })),
  ];

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
        What you have agreed to, and when. You can change your mind at any time.
      </p>

      {message && (
        <div
          className="rounded-xl border px-4 py-3 mb-4 text-sm"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'rgba(79, 93, 24,0.06)' }}
        >
          {message}
        </div>
      )}
      {error && (
        <div
          className="rounded-xl border px-4 py-3 mb-4 text-sm"
          style={{ borderColor: 'rgba(139, 61, 43,0.4)', color: 'var(--melio-charcoal)' }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>Loading…</p>
      ) : (
        <div className="space-y-3">
          {rows.map(({ type, withdrawable }) => {
            const label = LABELS[type];
            const summary = summaries[type];
            const status = summary?.status;
            if (!label) return null;
            return (
              <div
                key={type}
                className="rounded-xl border p-4"
                style={{ borderColor: 'var(--melio-border)' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-medium text-sm" style={{ color: 'var(--melio-charcoal)' }}>
                      {label.title}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
                      {label.body}
                    </p>
                  </div>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full border shrink-0"
                    style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}
                  >
                    {status ? STATUS_LABEL[status] : 'Not recorded'}
                  </span>
                </div>

                {withdrawable && status === 'granted' && (
                  confirming === type ? (
                    <div className="mt-3">
                      <p className="text-xs mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                        Your raw genetic data will be deleted and cannot be recovered — a new
                        analysis would need a new sample. The findings your clinician has already
                        reviewed stay on your record, and your membership is unaffected.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={busy === type}
                          onClick={() => void withdraw(type)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium"
                          style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
                        >
                          {busy === type ? 'Withdrawing…' : 'Withdraw and delete'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirming(null)}
                          className="px-3 py-1.5 rounded-lg text-xs"
                          style={{ color: 'var(--melio-text-muted)' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setConfirming(type); setMessage(null); }}
                      className="mt-3 text-xs underline"
                      style={{ color: 'var(--melio-text-muted)' }}
                    >
                      Withdraw this
                    </button>
                  )
                )}

                {!withdrawable && (
                  <p className="text-xs mt-2" style={{ color: 'var(--melio-text-muted)' }}>
                    Withdrawing this changes what Melio can provide, so a clinician talks it
                    through with you first. Message your care team and they will action it.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
